import { useEffect, useCallback, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { newInstanceId, realtimeChannelName } from '@/lib/realtime-channel';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface Notification {
  id: string;
  user_id: string;
  patient_id: string;
  type: string;
  title: string;
  message: string;
  task_execution_id: string | null;
  is_read: boolean;
  created_at: string;
}

const notificationsKey = (userId: string | undefined) => ['notifications', userId];

export const useNotifications = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: notifications = [], isLoading, refetch } = useQuery({
    queryKey: notificationsKey(user?.id),
    enabled: !!user,
    // Cache 5 min and dedupe across all NotificationBell mounts.
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false })
        .limit(50);
      if (error) {
        console.error('Error fetching notifications:', error);
        return [] as Notification[];
      }
      return (data || []) as Notification[];
    },
  });

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const setCache = useCallback(
    (updater: (prev: Notification[]) => Notification[]) => {
      queryClient.setQueryData<Notification[]>(notificationsKey(user?.id), (prev) =>
        updater(prev || [])
      );
    },
    [queryClient, user?.id]
  );

  const markAsRead = useCallback(
    async (notificationId: string) => {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId);
      if (error) {
        console.error('Error marking notification as read:', error);
        return;
      }
      setCache((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, is_read: true } : n))
      );
    },
    [setCache]
  );

  const markAllAsRead = useCallback(async () => {
    if (!user) return;
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('user_id', user.id)
      .eq('is_read', false);
    if (error) {
      console.error('Error marking all notifications as read:', error);
      return;
    }
    setCache((prev) => prev.map((n) => ({ ...n, is_read: true })));
  }, [user, setCache]);

  // Realtime subscription — unique channel per hook instance to avoid
  // "cannot add postgres_changes callbacks after subscribe()" when multiple
  // components (bell + list) mount this hook at the same time.
  const instanceIdRef = useRef<string>(newInstanceId());

  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(realtimeChannelName('notifications-realtime', user.id, instanceIdRef.current))
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const newNotification = payload.new as Notification;
          setCache((prev) =>
            prev.some((n) => n.id === newNotification.id) ? prev : [newNotification, ...prev]
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, setCache]);


  return {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    refetch,
  };
};
